export type UnitSystem = "imperial" | "metric"

export interface Measurement {
  quantity: number | null
  unit: string | null
}

// Canonical units (see Ingredient::UNIT_MAP) and their value in a metric base.
const VOLUME_ML: Record<string, number> = {
  tsp: 4.92892,
  tbsp: 14.7868,
  cup: 236.588,
  ml: 1,
  l: 1000,
}

const WEIGHT_G: Record<string, number> = {
  oz: 28.3495,
  lb: 453.592,
  g: 1,
  kg: 1000,
}

const METRIC_UNITS = new Set(["ml", "l", "g", "kg"])
const IMPERIAL_UNITS = new Set(["tsp", "tbsp", "cup", "oz", "lb"])

function round(value: number, step: number): number {
  // toFixed strips floating-point noise like 1.9000000000000001.
  return Number((Math.round(value / step) * step).toFixed(2))
}

function toMetricVolume(ml: number): Measurement {
  if (ml >= 1000) return { quantity: round(ml / 1000, 0.05), unit: "l" }
  return { quantity: ml >= 100 ? round(ml, 5) : round(ml, 1), unit: "ml" }
}

function toImperialVolume(ml: number): Measurement {
  if (ml >= 177) return { quantity: round(ml / 236.588, 0.25), unit: "cup" }
  if (ml >= 11) return { quantity: round(ml / 14.7868, 0.5), unit: "tbsp" }
  return { quantity: round(ml / 4.92892, 0.25), unit: "tsp" }
}

function toMetricWeight(g: number): Measurement {
  if (g >= 1000) return { quantity: round(g / 1000, 0.05), unit: "kg" }
  return { quantity: g >= 100 ? round(g, 5) : round(g, 1), unit: "g" }
}

function toImperialWeight(g: number): Measurement {
  if (g >= 453.592) return { quantity: round(g / 453.592, 0.1), unit: "lb" }
  return { quantity: round(g / 28.3495, 0.25), unit: "oz" }
}

// Converts a quantity + unit to the requested system. Units already in the
// target system (and count units like "clove" or "pinch") are returned as-is, so
// toggling back and forth always restores the recipe's original exact values.
export function convertMeasurement(
  quantity: number | null,
  unit: string | null,
  system: UnitSystem,
): Measurement {
  if (quantity == null || !unit) return { quantity, unit }

  const u = unit.toLowerCase()
  if (system === "metric" && METRIC_UNITS.has(u)) return { quantity, unit }
  if (system === "imperial" && IMPERIAL_UNITS.has(u)) return { quantity, unit }

  if (u in VOLUME_ML) {
    const ml = quantity * VOLUME_ML[u]
    return system === "metric" ? toMetricVolume(ml) : toImperialVolume(ml)
  }
  if (u in WEIGHT_G) {
    const g = quantity * WEIGHT_G[u]
    return system === "metric" ? toMetricWeight(g) : toImperialWeight(g)
  }

  return { quantity, unit }
}
